'use client'

import ConfirmModal from '@/components/confirm-modal'
import {
    MedicalEvolution,
    deleteEvolution,
    getEvolutionsByClient,
} from '@/lib/api/prontuario/prontuario-api'
import { format } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { useEffect, useState } from 'react'
import {
    MdAdd,
    MdCheck,
    MdDelete,
    MdEdit,
    MdPerson,
    MdSchedule,
    MdTrendingUp,
    MdVisibility,
} from 'react-icons/md'
import { toast } from 'react-toastify'
import { useProntuarioData, useProntuarioNavigate } from './prontuario-context'

export default function EvolucoesPage() {
    const navigate = useProntuarioNavigate()
    const { client, clientId } = useProntuarioData()

    const [evolutions, setEvolutions] = useState<MedicalEvolution[]>([])
    const [loading, setLoading] = useState(true)
    const [showDeleteModal, setShowDeleteModal] = useState(false)
    const [evolutionToDelete, setEvolutionToDelete] = useState<number | null>(
        null
    )

    const basePath = `/person/clients/${clientId}/prontuario`

    useEffect(() => {
        if (clientId) {
            loadEvolutions()
        }
    }, [clientId])

    const loadEvolutions = async () => {
        if (!clientId) return

        setLoading(true)
        try {
            const response = await getEvolutionsByClient(parseInt(clientId, 10))
            if (response.success) {
                setEvolutions(response.result || [])
            } else {
                toast.error(response.error || 'Erro ao carregar evoluções')
            }
        } catch (error) {
            toast.error('Erro ao carregar evoluções')
        } finally {
            setLoading(false)
        }
    }

    const handleDelete = (id: number) => {
        setEvolutionToDelete(id)
        setShowDeleteModal(true)
    }

    const confirmDelete = async () => {
        if (!evolutionToDelete) return

        try {
            const response = await deleteEvolution(evolutionToDelete)
            if (response.success) {
                toast.success('Evolução excluída com sucesso!')
                await loadEvolutions()
            } else {
                toast.error(response.error || 'Erro ao excluir evolução')
            }
        } catch (error) {
            toast.error('Erro ao excluir evolução')
        }
    }

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[300px] gap-4">
                <div className="loading loading-spinner loading-lg text-secondary"></div>
                <p className="text-base-content/60 text-sm">
                    Carregando evoluções...
                </p>
            </div>
        )
    }

    return (
        <div className="flex flex-col gap-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h2 className="text-xl font-bold text-base-content">
                        Evoluções
                    </h2>
                    <p className="text-sm text-base-content/60">
                        Registros de evolução médica (SOAP)
                    </p>
                </div>
                <button
                    onClick={() => navigate(`${basePath}/evolucoes/new`)}
                    className="btn btn-secondary gap-2"
                >
                    <MdAdd className="text-lg" />
                    Nova Evolução
                </button>
            </div>

            {/* Evolutions List */}
            {evolutions.length === 0 ? (
                <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-8 text-center">
                    <MdTrendingUp className="text-4xl mx-auto mb-3 text-base-content/20" />
                    <p className="text-base-content/60">
                        Nenhuma evolução registrada
                    </p>
                    <p className="text-sm text-base-content/40 mt-1">
                        Clique no botão acima para criar uma evolução
                    </p>
                </div>
            ) : (
                <div className="space-y-4">
                    {evolutions.map(evolution => (
                        <div
                            key={evolution.id}
                            className="bg-gradient-to-br from-base-100 to-base-200/30 rounded-xl border border-base-300/50 shadow-sm overflow-hidden"
                        >
                            <div className="p-5">
                                <div className="flex items-start justify-between">
                                    <div className="flex-1">
                                        <div className="flex items-center gap-3 mb-3">
                                            <div className="p-2 bg-secondary/10 rounded-lg">
                                                <MdTrendingUp className="text-xl text-secondary" />
                                            </div>
                                            <div>
                                                <div className="flex items-center gap-2">
                                                    <h3 className="font-bold text-base-content">
                                                        Evolução #{evolution.id}
                                                    </h3>
                                                    {evolution.signedAt ? (
                                                        <span className="badge badge-success badge-sm gap-1">
                                                            <MdCheck className="text-xs" />
                                                            Assinada
                                                        </span>
                                                    ) : (
                                                        <span className="badge badge-warning badge-sm">
                                                            Pendente
                                                        </span>
                                                    )}
                                                </div>
                                                <p className="text-sm text-base-content/60">
                                                    {format(
                                                        new Date(
                                                            evolution.evolutionDate
                                                        ),
                                                        "dd 'de' MMMM 'de' yyyy 'às' HH:mm",
                                                        { locale: ptBR }
                                                    )}
                                                </p>
                                            </div>
                                        </div>

                                        {/* SOAP Summary */}
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                                            {evolution.subjective && (
                                                <div className="bg-base-200/50 rounded-lg p-3">
                                                    <p className="text-xs font-bold text-secondary mb-1">
                                                        S - Subjetivo
                                                    </p>
                                                    <p className="text-sm text-base-content line-clamp-2">
                                                        {evolution.subjective}
                                                    </p>
                                                </div>
                                            )}

                                            {evolution.objective && (
                                                <div className="bg-base-200/50 rounded-lg p-3">
                                                    <p className="text-xs font-bold text-secondary mb-1">
                                                        O - Objetivo
                                                    </p>
                                                    <p className="text-sm text-base-content line-clamp-2">
                                                        {evolution.objective}
                                                    </p>
                                                </div>
                                            )}

                                            {evolution.assessment && (
                                                <div className="bg-base-200/50 rounded-lg p-3">
                                                    <p className="text-xs font-bold text-accent mb-1">
                                                        A - Avaliação
                                                    </p>
                                                    <p className="text-sm text-base-content line-clamp-2">
                                                        {evolution.assessment}
                                                    </p>
                                                </div>
                                            )}

                                            {evolution.plan && (
                                                <div className="bg-base-200/50 rounded-lg p-3">
                                                    <p className="text-xs font-bold text-info mb-1">
                                                        P - Plano
                                                    </p>
                                                    <p className="text-sm text-base-content line-clamp-2">
                                                        {evolution.plan}
                                                    </p>
                                                </div>
                                            )}
                                        </div>

                                        {/* Vital Signs */}
                                        {evolution.vitalSigns && (
                                            <div className="flex flex-wrap gap-2 mt-3">
                                                {evolution.vitalSigns
                                                    .bloodPressure && (
                                                    <span className="badge badge-ghost">
                                                        PA:{' '}
                                                        {
                                                            evolution.vitalSigns
                                                                .bloodPressure
                                                        }
                                                    </span>
                                                )}
                                                {evolution.vitalSigns
                                                    .heartRate && (
                                                    <span className="badge badge-ghost">
                                                        FC:{' '}
                                                        {
                                                            evolution.vitalSigns
                                                                .heartRate
                                                        }{' '}
                                                        bpm
                                                    </span>
                                                )}
                                                {evolution.vitalSigns
                                                    .temperature && (
                                                    <span className="badge badge-ghost">
                                                        Temp:{' '}
                                                        {
                                                            evolution.vitalSigns
                                                                .temperature
                                                        }
                                                        C
                                                    </span>
                                                )}
                                                {evolution.vitalSigns
                                                    .oxygenSaturation && (
                                                    <span className="badge badge-ghost">
                                                        SpO2:{' '}
                                                        {
                                                            evolution.vitalSigns
                                                                .oxygenSaturation
                                                        }
                                                        %
                                                    </span>
                                                )}
                                            </div>
                                        )}

                                        {/* Return Date */}
                                        {evolution.returnDate && (
                                            <div className="flex items-center gap-2 text-sm text-info mt-3">
                                                <MdSchedule className="text-base" />
                                                <span>
                                                    Retorno:{' '}
                                                    {format(
                                                        new Date(
                                                            evolution.returnDate
                                                        ),
                                                        'dd/MM/yyyy',
                                                        { locale: ptBR }
                                                    )}
                                                </span>
                                            </div>
                                        )}

                                        {evolution.professional && (
                                            <div className="flex items-center gap-2 text-sm text-base-content/60 mt-3 pt-3 border-t border-base-300/50">
                                                <MdPerson className="text-base" />
                                                <span>
                                                    {
                                                        evolution.professional
                                                            .name
                                                    }
                                                    {evolution.professional
                                                        .registrationNumber && (
                                                        <>
                                                            {' '}
                                                            -{' '}
                                                            {
                                                                evolution
                                                                    .professional
                                                                    .councilName
                                                            }{' '}
                                                            {
                                                                evolution
                                                                    .professional
                                                                    .registrationNumber
                                                            }
                                                        </>
                                                    )}
                                                </span>
                                            </div>
                                        )}
                                    </div>

                                    <div className="flex items-center gap-1">
                                        <button
                                            onClick={() =>
                                                navigate(
                                                    `${basePath}/evolucoes/${evolution.id}`
                                                )
                                            }
                                            className="btn btn-ghost btn-sm btn-square"
                                            title="Visualizar"
                                        >
                                            <MdVisibility className="text-lg" />
                                        </button>
                                        {!evolution.signedAt && (
                                            <>
                                                <button
                                                    onClick={() =>
                                                        navigate(
                                                            `${basePath}/evolucoes/${evolution.id}/editar`
                                                        )
                                                    }
                                                    className="btn btn-ghost btn-sm btn-square"
                                                    title="Editar"
                                                >
                                                    <MdEdit className="text-lg" />
                                                </button>
                                                <button
                                                    onClick={() =>
                                                        handleDelete(
                                                            evolution.id
                                                        )
                                                    }
                                                    className="btn btn-ghost btn-sm btn-square text-error"
                                                    title="Excluir"
                                                >
                                                    <MdDelete className="text-lg" />
                                                </button>
                                            </>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            <ConfirmModal
                isOpen={showDeleteModal}
                onClose={() => {
                    setShowDeleteModal(false)
                    setEvolutionToDelete(null)
                }}
                onConfirm={confirmDelete}
                title="Excluir evolução"
                message="Tem certeza que deseja excluir esta evolução?"
                confirmButtonText="Excluir"
                cancelButtonText="Cancelar"
            />
        </div>
    )
}
